import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi, useApiQuery } from "@/api/useApi";
import { useCatalog } from "@/api/catalog";
import type { ExpenseEntry, ReportSummary } from "@/api/types";
import { Icon } from "@/components/Icon";
import { ReceiptPreview } from "@/components/ReceiptPreview";
import { Sheet } from "@/components/Sheet";
import { dateRange, entryTypeLabel, money, shortDate } from "@/format";
import { ExpenseForm } from "@/pages/report/EntryForms";
import { useDialogs } from "@/components/dialogs";

/** One pending expense as a list row. Shared by Home and the report's Add sheet. */
export function PendingRow({ entry, onOpen }: { entry: ExpenseEntry; onOpen: () => void }) {
  const catalog = useCatalog();
  return (
    <li className="entry">
      <button type="button" className="entry-main" onClick={onOpen}>
        <span className="entry-icon">
          <Icon name="mail" />
        </span>
        <span className="entry-text">
          <strong>{entry.merchant}</strong>
          <span className="muted">
            {entryTypeLabel(entry, catalog)} · {shortDate(entry.incurredOn)}
          </span>
        </span>
        <span className="entry-amount">{money(entry.amountUsd)}</span>
      </button>
    </li>
  );
}

/**
 * Receipts forwarded by email while there was no draft to put them on, or by a delegate, who chooses
 * whose report each goes on. Each can be added to a draft, corrected, or removed.
 */
export function PendingExpenses({
  entries,
  userId,
  onChanged,
}: {
  entries: ExpenseEntry[];
  userId: string;
  onChanged: () => void;
}) {
  const api = useApi();
  // The person's own drafts first, then those of anyone they do expenses for.
  const { data: destinations, reload: reloadDrafts } = useApiQuery<ReportSummary[]>(
    entries.length > 0 ? "/api/expenses/pending/reports" : null,
  );
  const drafts = [...(destinations ?? [])].sort(
    (a, b) => Number(a.submitter.id !== userId) - Number(b.submitter.id !== userId),
  );
  const dialogs = useDialogs();
  const catalog = useCatalog();
  const [open, setOpen] = useState<ExpenseEntry>();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  if (entries.length === 0) return null;

  function close() {
    setOpen(undefined);
    setEditing(false);
    setError(undefined);
  }

  function act(work: Promise<unknown>) {
    setBusy(true);
    setError(undefined);
    work
      .then(() => {
        close();
        reloadDrafts();
        onChanged();
      })
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : String(caught)),
      )
      .finally(() => setBusy(false));
  }

  return (
    <section className="list-section">
      <h2 className="section-title">Pending · from email</h2>
      <ul className="card entry-list">
        {entries.map((entry) => (
          <PendingRow key={entry.id} entry={entry} onOpen={() => setOpen(entry)} />
        ))}
      </ul>

      <Sheet
        open={open !== undefined}
        title={editing ? "Edit expense" : "Pending expense"}
        onClose={close}
        confirmDiscard
      >
        {open && editing && (
          <ExpenseForm
            entry={open}
            onPendingSaved={(saved) => {
              setOpen(saved);
              setEditing(false);
              onChanged();
            }}
          />
        )}
        {open && !editing && (
          <div className="stack">
            <div className="row">
              <div>
                <strong>{open.merchant}</strong>
                <p className="muted">
                  {entryTypeLabel(open, catalog)} · {shortDate(open.incurredOn)}
                  {open.description ? ` · ${open.description}` : ""}
                </p>
              </div>
              <strong>{money(open.amountUsd)}</strong>
            </div>
            {open.receiptDoubt && (
              <div className="note warn">
                <strong>This might not be a receipt.</strong>
                <span>{open.receiptDoubt} Check it, and save it with Edit, or remove it.</span>
              </div>
            )}
            {open.receipt && <ReceiptPreview receipt={open.receipt} />}
            {error && (
              <div className="error" role="alert">
                {error}
              </div>
            )}
            <h2>Add to a report</h2>
            {drafts.length === 0 ? (
              <p className="muted">You have no draft reports.</p>
            ) : (
              <div className="choices">
                {drafts.map((draft) => (
                  <button
                    key={draft.id}
                    type="button"
                    className="choice"
                    disabled={busy}
                    onClick={() =>
                      act(api.post(`/api/expenses/pending/${open.id}/move`, { reportId: draft.id }))
                    }
                  >
                    <span className="choice-icon">
                      <Icon name="reports" size={22} />
                    </span>
                    <span>
                      <strong>{draft.title}</strong>
                      <span className="muted">
                        {draft.submitter.id === userId
                          ? ""
                          : `For ${draft.submitter.displayName} · `}
                        {dateRange(draft.periodStart, draft.periodEnd)}
                      </span>
                    </span>
                    <Icon name="chevronRight" size={18} />
                  </button>
                ))}
              </div>
            )}
            <Link className="btn secondary" to="/new">
              <Icon name="plus" size={18} /> New expense report
            </Link>
            <div className="entry-actions">
              <button
                type="button"
                className="btn secondary small"
                onClick={() => setEditing(true)}
              >
                <Icon name="pencil" size={16} /> Edit
              </button>
              <button
                type="button"
                className="btn ghost small"
                disabled={busy}
                onClick={() => {
                  void dialogs
                    .confirm({
                      title: `Remove ${open.merchant}?`,
                      confirmLabel: "Remove",
                      danger: true,
                    })
                    .then((yes) => {
                      if (yes) act(api.del(`/api/expenses/pending/${open.id}`));
                    });
                }}
              >
                <Icon name="trash" size={16} /> Remove
              </button>
            </div>
          </div>
        )}
      </Sheet>
    </section>
  );
}
